import React from 'react';
import {
  Box, Typography, Divider, Select, MenuItem, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  Snackbar, Alert, CircularProgress, Pagination
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getApolloHeader } from '../../api';
import { useState, useEffect } from 'react';
import useFetch from '../../hooks/useFetch';
import { setApolloHeaderStatus, getApolloRiskPriorityHistory } from '../../api';
import { Tooltip } from '@mui/material';
import ApptRangeButton from './ApptRangeButton';

const TealHeader = ({ patientId, username, token }) => {
  const { data: patient, loading, error } = useFetch(getApolloHeader, patientId, { username, token });
  const [priority, setPriority] = useState('');
  const [lockedUntil, setLockedUntil] = useState(null);
  const [lockedSince, setLockedSince] = useState(null);
  const [message, setMessage] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 3;
  const [pendingPriority, setPendingPriority] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);

  const riskOrder = ['Good', 'Fair', 'Serious', 'Critical', 'Palliative', 'Hospice'];
  // Lock-in weeks keyed by the level reached via a single-step de-escalation (mirrors backend).
  const lockWeeks = { Good: 9, Fair: 6, Serious: 3, Critical: 3, Palliative: 3 };

  const displayLabel = (value) =>
    value === 'Palliative' ? 'Palliative E' : value === 'Hospice' ? 'Hospice E' : value;
  const riskColor = (value) => {
    if (value === 'Serious') return '#b26a00';
    if (value === 'Critical' || value === 'Hospice' || value === 'Palliative') return '#c62828';
    if (value === 'Good') return '#2e7d32';
    return 'text.primary';
  };
  const directionMeta = (direction) => ({
    'escalation': { label: '▲ Escalated', color: '#c62828' },
    'de-escalation': { label: '▼ De-escalated', color: '#2e7d32' },
    'initial': { label: '● Set', color: 'text.secondary' },
  }[direction] || { label: direction, color: 'text.secondary' });
  // Backend timestamps are naive UTC (no tz suffix); mark them UTC so toLocale* renders
  // in the viewer's local time. Client-generated ISO strings already carry a 'Z'.
  const parseServerDate = (s) => {
    if (!s) return null;
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(s);
    return new Date(hasTz ? s : s + 'Z');
  };
  const formatDate = (iso) => (iso ? parseServerDate(iso).toLocaleDateString() : '');
  const isLocked = lockedUntil != null && parseServerDate(lockedUntil) > new Date();

  useEffect(() => {
    if (patient && patient.Priority) {
      setPriority(patient.Priority);
    }
    if (patient) {
      setLockedUntil(patient.LockedUntil || null);
      setLockedSince(patient.LockedSince || null);
    }
  }, [patient]);

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography>Error: {error.message}</Typography>;
  if (!patient.PatientID) return null;

  // Selecting a new level runs the step/lock pre-checks, then opens a note dialog;
  // nothing is committed until the user saves the note in confirmPriorityChange().
  const handlePriorityChange = (event) => {
    const newPriority = event.target.value;
    const currentIndex = riskOrder.indexOf(priority);
    const newIndex = riskOrder.indexOf(newPriority);
    if (newIndex === currentIndex) return;

    if (newIndex < currentIndex) { // de-escalation
      // Multi-step de-escalations are disabled in the dropdown (the reason shows in
      // the option's tooltip), so just ignore them here defensively.
      if (newIndex !== currentIndex - 1) return;
      if (isLocked) {
        setMessage(`Locked from further de-escalation until ${formatDate(lockedUntil)}.`);
        return;
      }
    }

    setNoteText('');
    setPendingPriority(newPriority);
  };

  const cancelPriorityChange = () => {
    setPendingPriority(null);
    setNoteText('');
  };

  const confirmPriorityChange = async () => {
    const newPriority = pendingPriority;
    const note = noteText.trim();
    if (!note) return; // a reason is required; Save is disabled until one is entered
    const isDeescalation = riskOrder.indexOf(newPriority) < riskOrder.indexOf(priority);
    setSaving(true);
    try {
      await setApolloHeaderStatus(patientId, { username, token }, newPriority, note);
      setPriority(newPriority);
      if (isDeescalation) {
        // Start a new lock window for this de-escalation.
        const until = new Date();
        until.setDate(until.getDate() + (lockWeeks[newPriority] || 0) * 7);
        setLockedSince(new Date().toISOString());
        setLockedUntil(until.toISOString());
      }
      // Escalation does NOT clear an active de-escalation lock: it persists for its
      // full window so escalating up and back down cannot bypass it.
      setPendingPriority(null);
      setNoteText('');
    } catch (err) {
      setMessage(err?.response?.data?.message || 'Failed to update risk priority.');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryPage(1);
    try {
      const data = await getApolloRiskPriorityHistory(patientId, { username, token });
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      setHistory([]);
      setMessage('Failed to load risk priority history.');
    } finally {
      setHistoryLoading(false);
    }
  };
  const renderFlagChips = () => {
    const flags = [];
  
    const style = {
      CHF: {
        label: 'CHF',
        bgColor: '#f8cccc',
        textColor: '#800000'
      },
      CKD: {
        label: 'CKD',
        bgColor: '#fff0b3',
        textColor: '#996600'
      },
      COPD: {
        label: 'COPD',
        bgColor: '#b3e5fc',
        textColor: '#004d66'
      }
    };
  
    if (patient.DxCHF && patient.DxCHF.trim() !== '1900-01-01') {
      flags.push(
        <Tooltip key="CHF" title={`CHF assessed on ${patient.DxCHF}`} arrow>
          <Chip
            label={style.CHF.label}
            size="small"
            sx={{
              backgroundColor: style.CHF.bgColor,
              color: style.CHF.textColor,
              fontWeight: 500,
              mr: 1
            }}
          />
        </Tooltip>
      );
    }
  
    if (patient.DxCKD && patient.DxCKD.trim() !== '1900-01-01') {
      flags.push(
        <Tooltip key="CKD" title={`CKD assessed on ${patient.DxCKD}`} arrow>
          <Chip
            label={style.CKD.label}
            size="small"
            sx={{
              backgroundColor: style.CKD.bgColor,
              color: style.CKD.textColor,
              fontWeight: 500,
              mr: 1
            }}
          />
        </Tooltip>
      );
    }
  
    if (patient.DxCOPD && patient.DxCOPD.trim() !== '1900-01-01') {
      flags.push(
        <Tooltip key="COPD" title={`COPD assessed on ${patient.DxCOPD}`} arrow>
          <Chip
            label={style.COPD.label}
            size="small"
            sx={{
              backgroundColor: style.COPD.bgColor,
              color: style.COPD.textColor,
              fontWeight: 500,
              mr: 1
            }}
          />
        </Tooltip>
      );
    }
  
    return flags.length > 0 ? (
      <Box mb={1} display="flex" flexWrap="wrap" gap={1}>
        {flags}
      </Box>
    ) : null;
  };
  

  return (
    <div>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        bgcolor="teal"
        color="white"
        p={1.5}
        borderRadius="4px 4px 0 0"
      >
        <Box display="flex" alignItems="center">
          <Box
            component="img"
            src="/icon.jpg"
            alt="Icon"
            width={25}
            height={40}
            mr={1}
          />
          <Box>
            <Typography variant="body1">{patient.PatientName} #{patient.PatientID}</Typography>
          </Box>
        </Box>
        <Box textAlign="left" sx={{ minWidth: 78, ml: 1 }}>
          <Typography variant="body2" sx={{ height: '20px' }}>{patient.Eligibility}</Typography>
          <Typography variant="body2" sx={{ height: '24px' }}>{patient.Dual}</Typography>
        </Box>
        <Box textAlign="left" sx={{ minWidth: 135 }}>
          <Typography variant="body2" component="div" sx={{ height: '20px' }}>vRAF: {patient?.VRAF}</Typography>
          <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center' }}>Risk:&nbsp;
            <Select
              value={priority}
              onChange={handlePriorityChange}
              renderValue={(val) => displayLabel(val)}
              IconComponent={() => null}
              sx={{
                fontSize: '0.75rem',
                padding: '0px 0px',
                height: '24px',
                lineHeight: '1',
                width: '95px',
                color: priority === 'Serious'
                  ? 'orange'
                  : priority === 'Critical' || priority === 'Hospice' || priority === 'Palliative'
                    ? '#ff6d6d'
                    : 'white',
                fontWeight: priority === 'Serious' || priority === 'Critical' || priority === 'Hospice' || priority === 'Palliative' ? 'bold' : 'inherit',
                marginLeft: 0,
                // No dropdown arrow: drop the padding MUI reserves for it.
                '& .MuiSelect-select': {
                  paddingRight: '8px !important',
                },
              }}
              inputProps={{
                sx: {
                  padding: '5px',
                  height: '24px',
                  lineHeight: 'normal',
                }
              }}
            >
              {riskOrder.map((value) => {
                const currentIndex = riskOrder.indexOf(priority);
                const i = riskOrder.indexOf(value);
                const label = displayLabel(value);

                // De-escalation options always render, but disabled (with a reason on
                // hover) when the change isn't currently permitted.
                let disabled = false;
                let tip = '';
                if (i < currentIndex) {
                  if (i < currentIndex - 1) {
                    disabled = true;
                    tip = 'De-escalation must be one level at a time.';
                  } else if (isLocked) {
                    disabled = true;
                    tip = `Locked until ${formatDate(lockedUntil)} — de-escalated on ${formatDate(lockedSince)}.`;
                  }
                }
                return (
                  <MenuItem
                    key={value}
                    value={value}
                    disabled={disabled}
                    sx={{
                      fontSize: '0.7rem',
                      minHeight: 'auto',
                      py: 0.5,
                      ...(disabled ? { '&.Mui-disabled': { pointerEvents: 'auto' } } : {}),
                    }}
                  >
                    {tip ? <Tooltip title={tip} arrow><span>{label}</span></Tooltip> : label}
                  </MenuItem>
                );
              })}
            </Select>
            <Tooltip title="View risk priority history" arrow>
              <IconButton size="small" onClick={openHistory} sx={{ color: 'white', ml: 0.5, p: 0.25 }}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
        </Box>
        <Box textAlign="left" />
      </Box>

      <Divider />

      <Box sx={{ backgroundColor: '#eaeded', px: 2, py: 1 }}>
        {renderFlagChips()}
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <ApptRangeButton
            patientId={patientId}
            username={username}
            token={token}
            moduleCategory="Header"
          />
          {patient.TapestryReview && (
            <Button
              variant="contained"
              size="small"
              endIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => window.open(`https://tapestry.sage.health/panel/${patientId}`, '_blank', 'noopener,noreferrer')}
              sx={{
                bgcolor: '#fce4ec',
                color: '#ad1457',
                fontWeight: 'bold',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#f8bbd0' },
              }}
            >
              Tapestry Review
            </Button>
          )}
        </Box>
      </Box>

      <Divider sx={{ height: 15 }} />

      <Dialog open={pendingPriority != null} onClose={cancelPriorityChange} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Change Risk Priority</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <Box component="span" sx={{ color: 'text.secondary' }}>{priority || '—'}</Box>
            {' → '}
            <Box component="span" sx={{ fontWeight: 'bold', color: riskColor(pendingPriority) }}>
              {displayLabel(pendingPriority || '')}
            </Box>
          </Typography>
          <TextField
            label="Note"
            placeholder="Reason for this change…"
            required
            multiline
            minRows={3}
            fullWidth
            size="small"
            autoFocus
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            helperText={noteText.trim() ? ' ' : 'A reason is required.'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelPriorityChange} disabled={saving}>Cancel</Button>
          <Button onClick={confirmPriorityChange} variant="contained" disabled={saving || !noteText.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Risk Priority History</DialogTitle>
        <DialogContent sx={{ px: 1.5 }}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
          ) : history.length === 0 ? (
            <Typography variant="body2">No changes recorded yet.</Typography>
          ) : (
            <Box>
              {history
                .slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE)
                .map((h, idx) => {
                const dir = directionMeta(h.direction);
                return (
                  <Box
                    key={idx}
                    sx={{
                      borderLeft: `3px solid ${dir.color === 'text.secondary' ? '#bdbdbd' : dir.color}`,
                      bgcolor: '#fafafa',
                      borderRadius: '0 4px 4px 0',
                      px: 1.25,
                      py: 1,
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 0.5 }}>
                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>
                        {h.old_status || '—'}
                      </Typography>
                      <Typography variant="body2" component="span" sx={{ color: 'text.secondary' }}>→</Typography>
                      <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', color: riskColor(h.new_status) }}>
                        {displayLabel(h.new_status)}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: dir.color, fontWeight: 500 }}>
                      {dir.label}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      by {h.changed_by} · {h.changed_at ? parseServerDate(h.changed_at).toLocaleString() : ''}
                    </Typography>
                    {h.note && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.primary', mt: 0.5, fontStyle: 'italic' }}>
                        “{h.note}”
                      </Typography>
                    )}
                  </Box>
                );
              })}
              {history.length > HISTORY_PAGE_SIZE && (
                <Box display="flex" justifyContent="center" mt={1}>
                  <Pagination
                    size="small"
                    count={Math.ceil(history.length / HISTORY_PAGE_SIZE)}
                    page={historyPage}
                    onChange={(_, p) => setHistoryPage(p)}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!message}
        autoHideDuration={5000}
        onClose={() => setMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setMessage('')} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TealHeader;
